/**
 * Per-open view state and keyboard navigation for `DatabaseSelectorDialog` —
 * filter text, the cursor, the refs the cursor scrolls and focuses, and the
 * arrow/Enter handling.
 *
 * Split out of the dialog when the shared-listing work pushed it past the
 * ~200-line guideline: none of this touches the listing, the failure affordance or
 * the *Set as default* rule, which is everything the component still decides.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

type UseDatabaseSelectorNavigationOptions = {
  /** Dialog open state — resets the filter and the cursor on each open. */
  open: boolean;
  /** Whether there is something to paint (not the initial load). */
  isLoaded: boolean;
  /** The rows on offer: the listing, or the unverified fallback entries. */
  options: string[];
  /** Explorer's suggestion, pre-selected when present in the filtered rows. */
  suggestedDatabase?: string | null;
  /**
   * Enter commits. Deliberately the dialog's RAW `onSelect`, not its
   * `handleSelect`: the keyboard path has never carried the *Set as default*
   * checkbox and this split does not change that.
   */
  onCommit: (database: string) => void;
};

export type DatabaseSelectorNavigation = {
  searchFilter: string;
  setSearchFilter: (value: string) => void;
  /** `true` past 10 rows — when the filter input is worth showing. */
  showSearch: boolean;
  filteredDatabases: string[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  itemRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  searchInputRef: React.RefObject<HTMLInputElement>;
  handleKeyDown: (event: React.KeyboardEvent) => void;
};

export function useDatabaseSelectorNavigation({
  open,
  isLoaded,
  options,
  suggestedDatabase,
  onCommit,
}: UseDatabaseSelectorNavigationOptions): DatabaseSelectorNavigation {
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredDatabases = useMemo(() => {
    if (!searchFilter.trim()) return options;
    const lower = searchFilter.toLowerCase();
    return options.filter((db) => db.toLowerCase().includes(lower));
  }, [options, searchFilter]);

  const showSearch = options.length > 10;

  // Reset the per-open view state whenever the dialog opens. The list itself is
  // no longer local state — it comes from the shared cache, so clearing it here
  // would only re-introduce the empty flash this change removes.
  useEffect(() => {
    if (!open) return;
    setSearchFilter('');
    setSelectedIndex(0);
  }, [open]);

  // Pre-select the suggested database when rows arrive, reset on filter change.
  // Keyed on the LENGTH, not the array: `options` is rebuilt whenever the fallback
  // presentation is, so depending on identity would reset the cursor under the
  // user's hands. Carried over as-is from the component, `react-hooks` warning
  // included — deliberately not silenced with a disable comment.
  useEffect(() => {
    if (suggestedDatabase && filteredDatabases.length > 0) {
      const idx = filteredDatabases.findIndex((db) => db === suggestedDatabase);
      setSelectedIndex(idx >= 0 ? idx : 0);
    } else {
      setSelectedIndex(0);
    }
  }, [filteredDatabases.length, suggestedDatabase]);

  // Scroll the cursor into view.
  useEffect(() => {
    if (open && isLoaded && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, open, isLoaded]);

  // Focus the filter input once there is a list to filter.
  useEffect(() => {
    if (open && isLoaded && showSearch) {
      // Small delay to ensure dialog is fully rendered
      const timeout = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [open, isLoaded, showSearch]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isLoaded || filteredDatabases.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredDatabases.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (filteredDatabases[selectedIndex]) {
          onCommit(filteredDatabases[selectedIndex]);
        }
        break;
    }
  };

  return {
    searchFilter,
    setSearchFilter,
    showSearch,
    filteredDatabases,
    selectedIndex,
    setSelectedIndex,
    itemRefs,
    searchInputRef,
    handleKeyDown,
  };
}
